/**
 * ConversationResponseScreen.js
 * ==============================
 * Patient gaze-operated response screen for Conversation Mode.
 *
 * Layout — same quad-card structure as ResponsePage, with:
 *   top    → "REGENERATE" (gaze up  → re-calls LLM with same caregiverText)
 *   left   → Response 1  (gaze left → TTS speaks, stays on page)
 *   right  → Response 2  (gaze right → TTS speaks, stays on page)
 *   bottom → Response 3  (gaze down → TTS speaks, stays on page)
 *
 * After a response is spoken the block stays highlighted (green) until the
 * caregiver starts a new turn or taps Regenerate.
 *
 * Return to listening screen: caregiver swipes up on the screen.
 *
 * Receives via React Router location.state:
 *   { responses: string[3], caregiverText: string }
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGazeNavigation } from './useGazeNavigation';
import { useGazeConfig, COLOR_MAP } from './GazeConfigContext';
import useReadAloud from './useReadAloud';
import { generateAiOptions } from './aiApi';
import './ConversationResponseScreen.css';
import './QuadrantPage.css';

const LONG_GAZE_FEEDBACK_MS = 400;
const CONFIRM_TIMEOUT_MS    = 45_000;

function speak(text, voice) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate   = 0.95;
  utt.pitch  = 1.0;
  utt.volume = 1.0;
  if (voice) utt.voice = voice;
  window.speechSynthesis.speak(utt);
}

export default function ConversationResponseScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { windowSize, config } = useGazeConfig();
  const isLongGaze = config.confirmationMode === 'long-gaze';

  const colorVars = {
    '--q-bg':   COLOR_MAP[config.backgroundColor] ?? '#ffffff',
    '--q-text': COLOR_MAP[config.textColor]        ?? '#000000',
  };

  // ── Pull initial data from router state ──────────────────────────────────
  const initialResponses    = location.state?.responses    ?? ['Yes', 'No', 'I need help'];
  const caregiverText       = location.state?.caregiverText ?? '';

  const [responses,     setResponses]     = useState(initialResponses);
  const [selectedIdx,   setSelectedIdx]   = useState(null); // index 0-2 of confirmed pick
  const [pendingIdx,    setPendingIdx]     = useState(null); // index being gaze-held
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError,    setRegenError]    = useState('');

  // ── Auto-announce response options when readAloud is on ──────────────────
  // Re-announces whenever responses change (e.g. after Regenerate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useReadAloud([
    responses[0] ? ['Look left for',  responses[0]] : null,
    responses[1] ? ['Look right for', responses[1]] : null,
    responses[2] ? ['Look down for',  responses[2]] : null,
  ].filter(Boolean), [responses.join('|')]);

  const pendingIdxRef   = useRef(null);
  const selectedIdxRef  = useRef(null);
  const pendingTimerRef  = useRef(null);

  useEffect(() => { pendingIdxRef.current = pendingIdx; }, [pendingIdx]);
  useEffect(() => { selectedIdxRef.current = selectedIdx; }, [selectedIdx]);
  useEffect(() => () => clearTimeout(pendingTimerRef.current), []);

  // ── Clear pending state ───────────────────────────────────────────────────
  function clearPending() {
    setPendingIdx(null);
    pendingIdxRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }

  // ── Confirm selection: speak text once, stay on page ─────────────────────
  function confirmSelection(idx) {
    const voice = config.voiceURI
      ? (window.speechSynthesis?.getVoices().find(v => v.voiceURI === config.voiceURI) ?? null)
      : null;
    speak(responses[idx], voice);
    setSelectedIdx(idx);
    clearPending();
  }

  // ── Handle gaze-triggered selection attempt ───────────────────────────────
  function handleSelect(idx) {
    if (pendingIdxRef.current !== null) return; // already waiting
    if (selectedIdxRef.current !== null) return; // response already confirmed

    setPendingIdx(idx);
    pendingIdxRef.current = idx;

    if (isLongGaze) {
      pendingTimerRef.current = setTimeout(() => {
        confirmSelection(idx);
      }, LONG_GAZE_FEEDBACK_MS);
    } else {
      // Double-blink mode: highlight, wait for blink or 45 s auto-cancel
      pendingTimerRef.current = setTimeout(clearPending, CONFIRM_TIMEOUT_MS);
    }
  }

  // ── Regenerate: re-call LLM with same caregiverText ──────────────────────
  const handleRegenerate = useCallback(async () => {
    if (isRegenerating) return;
    clearPending();
    setSelectedIdx(null);
    setRegenError('');
    setIsRegenerating(true);
    try {
      const newResponses = await generateAiOptions({ caregiverText, regenerate: true });
      setResponses(newResponses);
    } catch (err) {
      setRegenError('Regeneration failed — showing previous options.');
    } finally {
      setIsRegenerating(false);
    }
  }, [caregiverText, isRegenerating]);

  // ── Gaze navigation ───────────────────────────────────────────────────────
  const { videoRef, activeDirection, doubleBlinkCount } = useGazeNavigation({
    enabled: true,
    windowSize,
    onGazeSelect: (direction) => {
      // Double-blink pending: only allow cancellation via 'up'
      if (!isLongGaze && pendingIdxRef.current !== null) {
        if (direction === 'up') clearPending();
        return;
      }

      switch (direction) {
        case 'up':
          // Regenerate (NOT back-navigation — handled by swipe gesture instead)
          handleRegenerate();
          break;
        case 'left':
          handleSelect(0);
          break;
        case 'right':
          handleSelect(1);
          break;
        case 'down':
          handleSelect(2);
          break;
        default:
          break;
      }
    },
  });

  // ── Double-blink confirmation ─────────────────────────────────────────────
  useEffect(() => {
    if (doubleBlinkCount === 0) return;
    const idx = pendingIdxRef.current;
    if (idx === null) return;
    confirmSelection(idx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doubleBlinkCount]);

  // ── Swipe-up gesture → back to listen screen ─────────────────────────────
  const touchStartY = useRef(null);

  function onTouchStart(e) {
    touchStartY.current = e.touches[0].clientY;
  }

  function onTouchEnd(e) {
    if (touchStartY.current === null) return;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    if (dy > 80) {
      window.speechSynthesis?.cancel();
      navigate('/conversation/listen');
    }
    touchStartY.current = null;
  }

  // ── CSS helpers ───────────────────────────────────────────────────────────
  function blockClass(idx) {
    if (idx === selectedIdx) return 'quad-selected';
    if (pendingIdx === null && selectedIdx === null) return '';
    if (pendingIdx !== null) {
      return idx === pendingIdx ? 'quad-selected' : 'quad-dimmed';
    }
    return 'quad-dimmed';
  }

  function gazeClass(direction) {
    if (selectedIdx !== null) return '';
    if (!isLongGaze && pendingIdx !== null) return '';
    return activeDirection === direction ? 'quad-active' : '';
  }

  const [r0, r1, r2] = responses;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="quad-page crs-page"
      style={colorVars}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Hidden video feed for gaze detection */}
      <video ref={videoRef} playsInline muted
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />

      {/* ── Prompt bar ──────────────────────────────────────────────────────── */}
      <div className="crs-prompt-bar">
        <span className="crs-prompt-text">{caregiverText || '…'}</span>
        <button
          className="crs-mic-btn"
          onClick={() => {
            window.speechSynthesis?.cancel();
            navigate('/conversation/listen', { state: { autoStart: true } });
          }}
          aria-label="New prompt — return to listening"
        >
          {/* Microphone icon */}
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="11" rx="3" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <line x1="12" y1="17" x2="12" y2="21" />
            <line x1="9" y1="21" x2="15" y2="21" />
          </svg>
        </button>
      </div>

      {/* Error banner */}
      {regenError && (
        <div className="crs-error-banner" role="alert">{regenError}</div>
      )}

      {/* ── Quad card ─────────────────────────────────────────────────────── */}
      <div className="quad-card">

        {/* Top — REGENERATE */}
        <div
          className={`quad-top crs-regen-top ${gazeClass('up')} ${isRegenerating ? 'crs-regen-top--loading' : ''}`}
          onClick={handleRegenerate}
          role="button"
          aria-label="Regenerate responses"
        >
          <span className="quad-label crs-regen-label">
            {isRegenerating ? '…' : '↻ REGENERATE'}
          </span>
        </div>

        {/* Middle — Response 1 (left) + Response 2 (right) */}
        <div className="quad-middle">
          <div
            className={`quad-left ${blockClass(0)} ${gazeClass('left')}`}
            onClick={() => handleSelect(0)}
            role="button"
            aria-label={`Response option 1: ${r0}`}
          >
            <span className="quad-label">{r0}</span>
          </div>
          <div
            className={`quad-right ${blockClass(1)} ${gazeClass('right')}`}
            onClick={() => handleSelect(1)}
            role="button"
            aria-label={`Response option 2: ${r1}`}
          >
            <span className="quad-label">{r1}</span>
          </div>
        </div>

        {/* Bottom — Response 3 */}
        <div
          className={`quad-bottom ${blockClass(2)} ${gazeClass('down')}`}
          onClick={() => handleSelect(2)}
          role="button"
          aria-label={`Response option 3: ${r2}`}
        >
          <span className="quad-label">{r2}</span>
        </div>

      </div>
    </div>
  );
}
